import { useGetCrmDataQuery } from '../../../../generated/graphql';
import { CrmSegment } from './CrmSegment.enum';
import { SubscribedTable } from './SubscribedTable';
import { useCrmStore } from './CreatorCrm.store';
import { OtpTable } from './OtpTable';
import { FreeOffersTable } from './FreeOffersTable';
import { LeadsTable } from './LeadsTable';
import { AbandonedCartsTable } from './AbandonedCartsTable';
import { CreatorCrmLayout } from './CreatorCrm.layout';
import { CrmTableEmptyState } from './components/CrmTableEmptyState';
import {
  getAbandonedCartsUsers,
  getFreeOffersUsers,
  getLeadsUsers,
  getOtpUsers,
  getSubscribedUsers,
} from './CreatorCrm.api';
import { ComponentType, createRef, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { isOtpCreator } from '@client/new/common/offering.helpers';
import { pageBackground } from '@client/new/pages/page-background.css';
import { useUrlHash } from '@client/router/useUrlHash';

type Query = {
  page: number;
  pageSize: number;
};

export type FetchStatus = 'idle' | 'fetching' | 'resolved' | 'failed';

export type FetchSnapshot = {
  status: FetchStatus;
  wasFiltered: boolean;
};

const IDLE_SNAPSHOT: FetchSnapshot = { status: 'idle', wasFiltered: false };

const UsersTab = ({ influencerId }: { influencerId: number }) => {
  const tableRef = createRef();
  const crmStore = useCrmStore();
  const crmQuery = useGetCrmDataQuery({
    variables: {
      creatorId: influencerId,
    },
  });

  const filters = useCrmStore((s) => s.filters);
  const searchTerm = useCrmStore((s) => s.searchTerm);
  const refetchTableData = () => {
    tableRef.current && (tableRef.current as any).onQueryChange({ page: 0 });
  };
  useEffect(refetchTableData, [filters]);
  useEffect(() => {
    const debouncedRefetch = debounce(refetchTableData, 200);
    debouncedRefetch();
    return debouncedRefetch.cancel;
  }, [searchTerm]);

  const [totalResults, setTotalResults] = useState(0);
  const [fetchSnapshot, setFetchSnapshot] =
    useState<FetchSnapshot>(IDLE_SNAPSHOT);
  const fetchSeqRef = useRef(0);
  useEffect(() => {
    setFetchSnapshot(IDLE_SNAPSHOT);
    setTotalResults(0);
  }, [crmStore.segment]);

  const allowedSegments = [
    CrmSegment.Subscribed,
    CrmSegment.ExclusiveBuyers,
    CrmSegment.FreeOffers,
    CrmSegment.Leads,
    CrmSegment.AbandonedCarts,
  ];

  const [segmentHash] = useUrlHash();
  const isOtpOnly =
    isOtpCreator(crmQuery.data?.creator?.offering) &&
    (crmQuery.data?.creator?.offering || []).length === 1;
  useEffect(() => {
    if (crmQuery.loading) return;
    crmStore.setActiveSegment(
      allowedSegments.includes(segmentHash as CrmSegment)
        ? (segmentHash as CrmSegment)
        : isOtpOnly
          ? CrmSegment.ExclusiveBuyers
          : CrmSegment.Subscribed,
    );
  }, [segmentHash, crmQuery.loading, isOtpOnly]);

  if (crmQuery.loading) {
    return <div>Loading...</div>;
  }

  const isFilterQueryActive = () =>
    Boolean(crmStore.searchTerm) ||
    Object.values(crmStore.filters).some((values) => values.length > 0);

  const EMPTY_RESULT = { data: [], page: 0, totalCount: 0 };

  const trackFetch = async <T,>(
    query: Query,
    fetchPage: (pagination: {
      page: number;
      pageSize: number;
    }) => Promise<{ records: T[]; meta: { totalItems: number } }>,
  ) => {
    const page = query.page + 1;
    const seq = ++fetchSeqRef.current;
    const isLatest = () => seq === fetchSeqRef.current;
    // Captured at fetch start, not recomputed at render: the store may already
    // hold a newer search term (debounced 200ms) than the query that produced
    // the currently displayed result.
    const wasFiltered = isFilterQueryActive();
    setFetchSnapshot({ status: 'fetching', wasFiltered });

    try {
      const result = await fetchPage({ page, pageSize: query.pageSize });

      if (isLatest()) {
        setTotalResults(result.meta.totalItems);
        setFetchSnapshot({ status: 'resolved', wasFiltered });
      }

      return {
        data: result.records,
        page: page - 1,
        totalCount: result.meta.totalItems,
      };
    } catch {
      if (isLatest()) {
        setTotalResults(0);
        setFetchSnapshot({ status: 'failed', wasFiltered });
      }

      return EMPTY_RESULT;
    }
  };

  type SegmentTable = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: ComponentType<any>;
    getData: (
      query: Query,
    ) => Promise<{ data: unknown[]; page: number; totalCount: number }>;
  };

  const Tables: Record<
    Exclude<CrmSegment, CrmSegment.Cancelled>,
    SegmentTable
  > = {
    [CrmSegment.Subscribed]: {
      component: SubscribedTable,
      getData: (query: Query) =>
        trackFetch(query, (pagination) =>
          getSubscribedUsers(influencerId, {
            pagination,
            classification: crmStore.getClassification(),
            status: crmStore.filters.subscription,
            product: crmStore.filters.product,
            searchTerm: crmStore.searchTerm,
            label: crmStore.filters.additional,
          }),
        ),
    },
    [CrmSegment.ExclusiveBuyers]: {
      component: OtpTable,
      getData: (query: Query) =>
        trackFetch(query, (pagination) =>
          getOtpUsers(influencerId, {
            pagination,
            otpOffers: crmStore.filters.otpOffers,
            searchTerm: crmStore.searchTerm,
            status: crmStore.filters.subscription,
          }),
        ),
    },
    [CrmSegment.FreeOffers]: {
      component: FreeOffersTable,
      getData: (query: Query) =>
        trackFetch(query, (pagination) =>
          getFreeOffersUsers(influencerId, {
            pagination,
            freeOffers: crmStore.filters.freeOffers,
            convertedTo: crmStore.filters.convertedTo,
            conversionStatus: crmStore.filters.conversionStatus,
            searchTerm: crmStore.searchTerm,
          }),
        ),
    },
    [CrmSegment.Leads]: {
      component: LeadsTable,
      getData: (query: Query) =>
        trackFetch(query, (pagination) =>
          getLeadsUsers(influencerId, {
            pagination,
            searchTerm: crmStore.searchTerm,
            source: crmStore.filters.leadsSource,
          }),
        ),
    },
    [CrmSegment.AbandonedCarts]: {
      component: AbandonedCartsTable,
      getData: (query: Query) =>
        trackFetch(query, (pagination) =>
          getAbandonedCartsUsers(influencerId, {
            pagination,
            searchTerm: crmStore.searchTerm,
            conversionStatus: crmStore.filters.conversionStatus,
          }),
        ),
    },
  };

  const activeSegment = (crmStore.segment ||
    CrmSegment.Subscribed) as keyof typeof Tables;
  const selectedTable = Tables[activeSegment];

  return (
    <div className="overflow-x-auto">
      <selectedTable.component
        tableRef={tableRef}
        data={selectedTable.getData}
        totalResults={totalResults}
        isFetching={fetchSnapshot.status === 'fetching'}
        creator={crmQuery.data?.creator}
        emptyState={
          <CrmTableEmptyState
            segment={activeSegment}
            snapshot={fetchSnapshot}
          />
        }
      />
    </div>
  );
};

export const CreatorCrmPage = () => {
  const influencerId = useInfluencerId();

  return (
    <div className={pageBackground}>
      <CreatorCrmLayout influencerId={influencerId}>
        <UsersTab influencerId={influencerId} />
      </CreatorCrmLayout>
    </div>
  );
};
